// src/helpers/fake_crm_backend.js
//
// Drop-in fake backend for CRM (Contacts, Companies, Leads, Deals).
// No network calls — everything is stored in localStorage so your
// data survives page refreshes during development.
//
// Response shapes match exactly what src/slices/crm/reducer.js expects,
// so no changes are needed in thunk.js, reducer.js, or any component.

const delay = (data, ms = 400) =>
    new Promise((resolve) => setTimeout(() => resolve(data), ms));

const load = (key, seed) => {
    try {
        const raw = localStorage.getItem(key);
        if (raw) return JSON.parse(raw);
    } catch (e) {
        // ignore corrupt storage, fall back to seed
    }
    localStorage.setItem(key, JSON.stringify(seed));
    return seed;
};

const save = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
};

const genId = () => Date.now().toString() + Math.floor(Math.random() * 1000);

// ---------------- Seed Data ----------------

const seedContacts = [
    {
        _id: "1",
        name: "Alexis Clarke",
        company: "Themesbrand",
        designation: "Web Designer",
        email: "alexisclarke@velzon.com",
        phone: "+(256) 2451 2154",
        lead_score: "154",
        tags: ["Lead", "Partner"],
        last_contacted: "2021-04-13T18:30:00.000Z",
        image_src: "avatar-1.jpg",
    },
    {
        _id: "2",
        name: "Cristopher Meyer",
        company: "Zoetic Fashion",
        designation: "Marketing Head",
        email: "cristophermeyer@velzon.com",
        phone: "+(354) 6523 1594",
        lead_score: "68",
        tags: ["Exiting"],
        last_contacted: "2021-05-01T10:15:00.000Z",
        image_src: "avatar-2.jpg",
    },
    {
        _id: "3",
        name: "Vincent Solomon",
        company: "Digital Nomad",
        designation: "Web Designer & Developer",
        email: "vincentsolomon@velzon.com",
        phone: "+(422) 5124 9635",
        lead_score: "112",
        tags: ["Long-term"],
        last_contacted: "2021-06-11T14:00:00.000Z",
        image_src: "avatar-3.jpg",
    },
];

const seedLeads = [
    {
        _id: "1",
        name: "Donald Nelson",
        company: "Themesbrand",
        score: "82",
        phone: "+(255) 6541 2569",
        location: "Los Angeles",
        tags: ["New"],
        date: "10 Jan, 2022",
        image_src: "avatar-4.jpg",
    },
    {
        _id: "2",
        name: "Zaid Meyer",
        company: "Zoetic Fashion",
        score: "35",
        phone: "+(354) 6523 1594",
        location: "New York",
        tags: ["Lost"],
        date: "22 Feb, 2022",
        image_src: "avatar-5.jpg",
    },
    {
        _id: "3",
        name: "Alice Steward",
        company: "Digital Nomad",
        score: "74",
        phone: "+(422) 5124 9635",
        location: "London",
        tags: ["Qualified"],
        date: "05 Mar, 2022",
        image_src: "avatar-6.jpg",
    },
];

const seedCompanies = [
    {
        _id: "1",
        name: "Themesbrand",
        email: "info@themesbrand.com",
        phone: "+(256) 2451 2154",
        location: "Los Angeles",
        amount: "$154,000",
        tags: ["Client"],
        image_src: "avatar-1.jpg",
    },
    {
        _id: "2",
        name: "Zoetic Fashion",
        email: "info@zoetic.com",
        phone: "+(354) 6523 1594",
        location: "New York",
        amount: "$68,000",
        tags: ["Prospect"],
        image_src: "avatar-2.jpg",
    },
];

const seedDeals = [
    { _id: "1", name: "Website Redesign", value: "$12,000", stage: "Negotiation" },
    { _id: "2", name: "Mobile App", value: "$25,000", stage: "Proposal" },
];

let contacts = load("mock_crm_contacts", seedContacts);
let leads = load("mock_crm_leads", seedLeads);
let companies = load("mock_crm_companies", seedCompanies);
let deals = load("mock_crm_deals", seedDeals);

// ---------------- Contacts ----------------

export const getContacts = () => delay({ data: contacts });

export const addNewContact = (contact) => {
    const newContact = { ...contact, _id: contact._id || genId() };
    contacts = [newContact, ...contacts];
    save("mock_crm_contacts", contacts);
    return delay({ data: newContact });
};

export const updateContact = (contact) => {
    contacts = contacts.map((c) =>
        c._id.toString() === contact._id.toString() ? { ...c, ...contact } : c
    );
    save("mock_crm_contacts", contacts);
    return delay({ data: contact });
};

export const deleteContact = (id) => {
    contacts = contacts.filter((c) => c._id.toString() !== id.toString());
    save("mock_crm_contacts", contacts);
    return delay({ success: true });
};

// ---------------- Companies ----------------

export const getCompanies = () => delay({ data: companies });

export const addNewCompanies = (company) => {
    const newCompany = { ...company, _id: company._id || genId() };
    companies = [newCompany, ...companies];
    save("mock_crm_companies", companies);
    // Note: no { data: ... } wrapper here — matches reducer's
    // addNewCompanies.fulfilled, which uses action.payload directly.
    return delay(newCompany);
};

export const updateCompanies = (company) => {
    companies = companies.map((c) =>
        c._id === company._id ? { ...c, ...company } : c
    );
    save("mock_crm_companies", companies);
    return delay({ data: company });
};

export const deleteCompanies = (id) => {
    companies = companies.filter((c) => c._id.toString() !== id.toString());
    save("mock_crm_companies", companies);
    return delay({ success: true });
};

// ---------------- Deals ----------------

// Note: no { data: ... } wrapper — matches reducer's
// getDeals.fulfilled, which uses action.payload directly.
export const getDeals = () => delay(deals);

// ---------------- Leads ----------------

export const getLeads = () => delay({ data: leads });

export const addNewLead = (lead) => {
    const newLead = { ...lead, _id: lead._id || genId() };
    leads = [newLead, ...leads];
    save("mock_crm_leads", leads);
    return delay({ data: newLead });
};

export const updateLead = (lead) => {
    leads = leads.map((l) =>
        l._id.toString() === lead._id.toString() ? { ...l, ...lead } : l
    );
    save("mock_crm_leads", leads);
    return delay({ data: lead });
};

export const deleteLead = (id) => {
    leads = leads.filter((l) => l._id.toString() !== id.toString());
    save("mock_crm_leads", leads);
    return delay({ success: true });
};